import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { SANS_CATEGORIE } from '../../constants/filtres';
import AppFilterGrid, { AppFilterResetButton } from '../layout/AppFilterGrid';
import { GeneriqueDataType } from '../../constants/enums';
import { FamilleData } from '../../models/models';
import FamilleAutoC from '../AutoComplete/FamilleAutoC';
import { useSelector } from 'react-redux';
import { RootState } from '../../reduxStore/Store';
import SelectFiltrePartage from '../Selects/SelectFiltrePartage';

/**
 * Filtres UI pour la page Gestion Tiers.
 * Le filtre `type` n'est pas exposé ici : il est imposé par la route
 * (/client ou /fournisseur) et passé en prop par GestionTiers.
 */
export interface TiersFilterValues {
  nom:          string | null;
  email:        string | null;
  telephone:    string | null;
  categorie_id: number | null;
  partage:      number[];   // filtre « Partage » (0132) — mode « Tous départements » uniquement
}

interface TiersFiltersProps {
  values:   TiersFilterValues;
  onChange: (values: TiersFilterValues) => void;
  onReset:  () => void;
  /** Type de catégorie à filtrer (CATEGORIE_CLIENT/FOURNISSEUR) — absent = pas de filtre catégorie. */
  categorieType?: GeneriqueDataType | null;
}

const DEBOUNCE_MS = 400;

const TiersFilters: React.FC<TiersFiltersProps> = ({ values, onChange, onReset, categorieType }) => {
  // État local pour saisie immédiate, debounce pour limiter les appels API
  const [nomInput,       setNomInput]       = useState(values.nom       ?? '');
  const [emailInput,     setEmailInput]     = useState(values.email     ?? '');
  const [telephoneInput, setTelephoneInput] = useState(values.telephone ?? '');
  // Catégorie sélectionnée (objet pour l'autocomplete) — sélection immédiate, pas de debounce
  const [selectedCategorie, setSelectedCategorie] = useState<FamilleData | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAllMode = useSelector((state: RootState) => state.AppSlice.isAllMode);

  const triggerChange = (field: keyof TiersFilterValues, value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...values, [field]: value || null });
    }, DEBOUNCE_MS);
  };

  // Sync état local si reset externe
  useEffect(() => {
    setNomInput(values.nom ?? '');
    setEmailInput(values.email ?? '');
    setTelephoneInput(values.telephone ?? '');
    if (values.categorie_id == null) setSelectedCategorie(null);
  }, [values.nom, values.email, values.telephone, values.categorie_id]);

  const handleReset = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onReset();
  };

  return (
    <AppFilterGrid>
      <TextField
        label="Nom / Prénom"
        size="small"
        fullWidth
        value={nomInput}
        onChange={(e) => { setNomInput(e.target.value); triggerChange('nom', e.target.value); }}
      />

      <TextField
        label="Email"
        size="small"
        fullWidth
        value={emailInput}
        onChange={(e) => { setEmailInput(e.target.value); triggerChange('email', e.target.value); }}
      />

      <TextField
        label="Téléphone"
        size="small"
        fullWidth
        value={telephoneInput}
        onChange={(e) => { setTelephoneInput(e.target.value); triggerChange('telephone', e.target.value); }}
      />

      {categorieType && (
        <FamilleAutoC
          slot="parent"
          type={categorieType}
          label="Catégorie"
          selectedFamille={selectedCategorie}
          onFamilleChange={(c) => {
            setSelectedCategorie(c);
            onChange({ ...values, categorie_id: c ? c.id : null });
          }}
          disabled={values.categorie_id === SANS_CATEGORIE}
        />
      )}

      {/* categorie_id = 0 → tiers sans catégorie (migration 0133) */}
      {categorieType && (
        <FormControlLabel
          label="Sans catégorie"
          control={
            <Checkbox
              size="small"
              checked={values.categorie_id === SANS_CATEGORIE}
              onChange={(e) => {
                setSelectedCategorie(null);
                onChange({ ...values, categorie_id: e.target.checked ? SANS_CATEGORIE : null });
              }}
            />
          }
        />
      )}

      {isAllMode && (
        <SelectFiltrePartage
          value={values.partage}
          onChange={(partage) => onChange({ ...values, partage })}
        />
      )}

      <AppFilterResetButton onClick={handleReset} />
    </AppFilterGrid>
  );
};

export default TiersFilters;
